import { useEffect, useState } from 'react'
import { useAtomValue } from 'jotai'
import { useNotebookStore } from '@datalayer/jupyter-react'
import type { INotebookContent } from '@jupyterlab/nbformat'
import { ChallengeService } from '@duck/api-definitions'
import { CardButton } from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { currentSolutionAtom } from '@/store/saveWorkAtoms'
import { WysiwygEditor } from '@/components/WysiwygEditor'
import { ReadOnlyPythonNotebook } from '@/components/PythonNotebook/ReadOnlyPythonNotebook'
import { StandardLoader } from '@/components/StandardLoader'
import { PencilSquareIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { TriangleAlert } from 'lucide-react'
import { workspaceSolutionReviewVisibleAtom } from '@/store/workspaceAtoms'
import { toast } from 'sonner'

const SUBMIT_COOLDOWN_MS = 3000
const SOURCE_NOTEBOOK_ID = 'python-notebook'

interface SolutionReviewProps {
  charts: ChallengeService.WorkState['charts']
  challenge: ChallengeService.Challenge
  onKeepWorking: () => void
  onSubmit: () => Promise<void>
  isSubmitting: boolean
}

export const SolutionReview = ({
  charts = {},
  challenge,
  onKeepWorking,
  onSubmit,
  isSubmitting,
}: SolutionReviewProps) => {
  const { t } = useTar()
  const isSolutionReviewVisible = useAtomValue(workspaceSolutionReviewVisibleAtom)
  const solution = useAtomValue(currentSolutionAtom)
  const notebookStore = useNotebookStore()
  const [submitReady, setSubmitReady] = useState(false)
  const [notebookSnapshot, setNotebookSnapshot] = useState<INotebookContent | undefined>(undefined)
  const isPython =
    challenge.workState?.chosenWeapon === ChallengeService.TaskWeaponsOfChoiceEnum.Python

  useEffect(() => {
    if (!isSolutionReviewVisible) return
    setSubmitReady(false)
    const id = setTimeout(() => setSubmitReady(true), SUBMIT_COOLDOWN_MS)
    return () => clearTimeout(id)
  }, [isSolutionReviewVisible, setSubmitReady])

  // Snapshot the live notebook each time the review opens; fall back to saved
  // workState. The live model is only available inside the workspace.
  useEffect(() => {
    if (!isSolutionReviewVisible) return
    const live = notebookStore
      .selectNotebook(SOURCE_NOTEBOOK_ID)
      ?.adapter?.panel?.model?.toJSON() as INotebookContent | undefined
    const saved = challenge.workState?.jNotebook as INotebookContent | undefined
    setNotebookSnapshot(live ?? saved)
  }, [isSolutionReviewVisible, notebookStore, challenge])

  const canSubmit = submitReady && !isSubmitting

  const handleSubmit = () => {
    if (canSubmit) {
      onSubmit()
    }
  }

  const handleMisclickWarning = () => {
    toast.warning(t('challenges.misclickWarning'), {
      duration: 5_000,
      position: 'bottom-center'
    })
  }

  return (
    <div className="flex flex-col h-full min-h-0 bg-background">
      {/* Header with title */}
      <div className="flex-shrink-0 px-6 py-6 border-b border-border space-y-1">
        <h2 className="text-xl font-semibold text-foreground">
          {t('challenges.solutionReview')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('challenges.solutionReviewDescription')}
        </p>
        {!challenge.assignmentId && (
          <div className="text-warning pt-3 flex items-center gap-2">
            <TriangleAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p className="text-xs">{t('challenges.submissionCooldownDescription')}</p>
          </div>
        )}
      </div>

      {/* Solution content - takes remaining space */}
      <div className="flex-1 min-h-0 overflow-hidden justify-center items-center flex flex-col">
        {isPython ? (
          <ReadOnlyPythonNotebook notebook={notebookSnapshot} />
        ) : (
          <WysiwygEditor
            content={solution}
            readOnly
            startingCharts={charts}
            className="flex-1 min-h-0 p-0 border-0"
            editorClassName="justify-center items-center"
          />
        )}
      </div>

      {/* Actions section */}
      <div className="flex-shrink-0 p-4 border-t border-border space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <CardButton
            icon={<PencilSquareIcon className="w-6 h-6 text-muted-foreground" />}
            title={t('challenges.keepWorkingOnSolution')}
            variant="muted"
            onClick={onKeepWorking}
            disabled={isSubmitting}
            className='p-4'
          />
          <CardButton
            icon={
              isSubmitting ? (
                <StandardLoader size="sm" className="text-primary-foreground" />
              ) : (
                <CheckCircleIcon className="w-6 h-6 text-accent" />
              )
            }
            title={
              isSubmitting ? t('common.submitting') : t('challenges.readyToSubmit')
            }
            variant="default"
            onClick={canSubmit ? handleSubmit : handleMisclickWarning}
            className='p-4'
          />
        </div>
      </div>
    </div>
  )
}
